import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import CssBaseline from '@material-ui/core/CssBaseline';
import { Container } from '@material-ui/core';
import { ThemeProvider } from '@material-ui/core/styles';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import Amplify from 'aws-amplify';
import awsExports from './aws-exports';
import theme from './utils/theme';
import ScreensError from './screens/Error';
import { ModalContext } from './contexts/ModalContext';
import Modal from './components/Common/Modal/Modal';
import { DEFAULT_MODAL_CONTENT } from './utils/constants';
import { AppContainerStyle } from './App.style';
import { MembershipContext } from './contexts/MembershipContext';
import MembershipRoute from './components/Common/MembershipRoute/MembershipRoute';
import SSORoute from './components/Common/SSORoute/SSORoute';
import ScreensRenew from './screens/Renew';
import WithLoading from './hocs/WithLoading/WithLoading';
import ScreensJoin from './screens/Join';
import ScreensActive from './screens/Active/Active';
import { BackdropContext } from './contexts/BackdropContext';
import WithBackdropContext from './hocs/WithBackdropContext/WithBackdropContext';
import CustomBackdrop from './components/Common/CustomBackdrop/CustomBackdrop';
import ScreensHome from './screens/Home';
import AccountRecovery from './screens/AccountRecovery';
import AccountRecoveryAttempt from './screens/AccountRecoveryAttempt';
import DuesEstimator from './screens/DuesEstimator';
import MainLayout from './components/Common/MainLayout/MainLayout';
import { AuthProvider } from './contexts/AuthContext';
import ScreensCesAudit from './screens/CesAudit';
import ScreensUnderAudit from './screens/UnderAudit';
import { MemberOrderResponse } from 'data_types/member-order';
import { BackdropOptions } from 'data_types/backdrop-options';
import { GlobalValue } from 'data_types/global-value';
import LandingEpaymentInProcess from 'screens/LandingEpaymentInProcess';
import SSOCallbackRoute from 'components/Common/SSOCallbackRoute/SSOCallbackRoute';
import SSOLogoutCallbackRoute from 'components/Common/SSOLogoutCallbackRoute/SSOLogoutCallbackRoute';
import getMaintenanceBanner from './utils/get-maintenance-banner';

const ScreensHomeWithLoading = WithLoading(ScreensHome);
const ScreensRenewWithLoading = WithLoading(ScreensRenew);
const ScreensJoinWithLoading = WithLoading(ScreensJoin);
const ScreensActiveWithLoading = WithLoading(ScreensActive);
const ScreensCesAuditWithLoading = WithLoading(ScreensCesAudit);
const ScreensUnderAuditWithLoading = WithLoading(ScreensUnderAudit);

const App = (): JSX.Element => {
    const [modalOptions, setModalOptions] = useState(DEFAULT_MODAL_CONTENT);
    const [backdropOptions, setBackdropOptions] = useState({ isOpen: false } as BackdropOptions);
    const [membershipInfo, setMembershipInfo] = useState((null as unknown) as MemberOrderResponse);
    const [countries, setCountries] = useState((null as unknown) as GlobalValue[]);
    const [careerTypes, setCareerTypes] = useState((null as unknown) as GlobalValue[]);
    const [maintenanceBannerHtml, setMaintenanceBannerHtml] = useState('');
    const ModalWithContext = WithBackdropContext('modal', ModalContext, Modal);
    const BackdropWithContext = WithBackdropContext(
        'backdrop',
        BackdropContext as React.Context<{
            options: BackdropOptions;
            setOptions: Dispatch<SetStateAction<BackdropOptions>>;
        }>,
        CustomBackdrop,
    );
    const containerClass = AppContainerStyle();

    useEffect(() => {
        getMaintenanceBanner().then((html) => {
            setMaintenanceBannerHtml(html);
        });
    }, []);

    return (
        <Router>
            <ThemeProvider theme={theme}>
                <BackdropContext.Provider value={{ options: backdropOptions, setOptions: setBackdropOptions }}>
                    <ModalContext.Provider value={{ options: modalOptions, setOptions: setModalOptions }}>
                        <MembershipContext.Provider
                            value={{
                                membershipInfo,
                                setMembershipInfo,
                                countries,
                                setCountries,
                                careerTypes,
                                setCareerTypes,
                            }}
                        >
                            <AuthProvider>
                                <CssBaseline />
                                {maintenanceBannerHtml && (
                                    <div
                                        className={containerClass.maintenanceBanner}
                                        dangerouslySetInnerHTML={{ __html: maintenanceBannerHtml }}
                                    />
                                )}
                                <MainLayout>
                                    <BackdropWithContext />
                                    <ModalWithContext />
                                    <Container classes={{ root: containerClass.root }} maxWidth="lg">
                                    <Switch>
                                        <MembershipRoute exact path="/">
                                            <ScreensHomeWithLoading />
                                        </MembershipRoute>
                                        <MembershipRoute exact path="/epayment-in-progress-or-pending">
                                            <LandingEpaymentInProcess />
                                        </MembershipRoute>
                                        <MembershipRoute exact path="/renew">
                                            <ScreensRenewWithLoading />
                                        </MembershipRoute>
                                        <MembershipRoute exact path="/join">
                                            <ScreensJoinWithLoading />
                                        </MembershipRoute>
                                        <MembershipRoute exact path="/restart">
                                            <ScreensJoinWithLoading />
                                        </MembershipRoute>
                                        <MembershipRoute exact path="/active">
                                            <ScreensActiveWithLoading />
                                        </MembershipRoute>
                                        <MembershipRoute exact path="/ces-audit">
                                            <ScreensCesAuditWithLoading />
                                        </MembershipRoute>
                                        <MembershipRoute exact path="/under-audit">
                                            <ScreensUnderAuditWithLoading />
                                        </MembershipRoute>
                                        <Route exact path="/dues-estimator">
                                            <DuesEstimator />
                                        </Route>
                                        <Route exact path="/account-recovery">
                                            <AccountRecovery />
                                        </Route>
                                        <Route exact path="/account-recovery-attempt">
                                            <AccountRecoveryAttempt />
                                        </Route>
                                        <SSORoute exact path="/sso" />
                                        <SSOCallbackRoute exact path="/sso/callback" />
                                        <SSOLogoutCallbackRoute exact path="/sso/logout/callback" />
                                        <Route>
                                            <ScreensError
                                                {...{
                                                    errorHeader: '404 Not Found',
                                                    errorMessage: 'We cannot find the page you are looking for',
                                                }}
                                            />
                                        </Route>
                                        </Switch>
                                    </Container>
                                </MainLayout>
                            </AuthProvider>
                        </MembershipContext.Provider>
                    </ModalContext.Provider>
                </BackdropContext.Provider>
            </ThemeProvider>
        </Router>
    );
};

// Allow overriding the REST API endpoint via environment variable
const endpointOverride = process.env.REACT_APP_MEMBERSHIP_API_URL;
if (endpointOverride) {
    awsExports.aws_cloud_logic_custom[0].endpoint = endpointOverride;
}

Amplify.configure(awsExports);

export default App;
